import { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button } from "@tellescope/design-system";
import { useConfirmStripePayment } from "../useConfirmStripePayment";

interface ShopPaymentElementFormProps {
  businessName: string;
  onConfirmed: () => void;
}

/**
 * Payment Element form for the Stripe Connect checkout path (prepare_stripe_checkout's clientSecret
 * is a PaymentIntent here, not an embedded Checkout Session — see PaymentSummary's isCheckout branch).
 * Always confirms immediately (the shop has no save-for-later flow), unlike the more general
 * StripePaymentForm in packages/apis/forms, which also supports a setup-intent/save path.
 */
export const ShopPaymentElementForm = ({ businessName, onConfirmed }: ShopPaymentElementFormProps) => {
  const { t } = useTranslation("shop");
  const stripe = useStripe();
  const elements = useElements();

  const confirmPaymentMutation = useConfirmStripePayment(stripe, elements, onConfirmed);

  // Stripe writes card and validation error messages for the customer (e.g. "Your card was
  // declined."); any other error is internal, so it gets the generic message instead.
  const { error } = confirmPaymentMutation;
  const isCustomerFacingStripeError =
    !!error && "type" in error && (error.type === "card_error" || error.type === "validation_error");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    confirmPaymentMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ business: { name: businessName } }} />
      <Button type="submit" className="w-full" size="lg" loading={confirmPaymentMutation.isPending} disabled={!stripe}>
        {t("Place Order")}
      </Button>
      {error && (
        <p className="text-sm text-destructive text-center">
          {isCustomerFacingStripeError && error.message
            ? error.message
            : t("We couldn't complete your payment. Please try again or contact your care team.")}
        </p>
      )}
    </form>
  );
};
